import axios from "axios"

class CurrencyHelper {
  constructor() {
    this.currencyInfo = null
    this.pendingFetch = null
    // 各貨幣顯示精度 (小數位數) - 與後端保持一致
    this.CURRENCY_PRECISION = {
      'TWD': 0,
      'HKD': 0,
      'CNY': 0,
      'JPY': 0,
      'USD': 0
    }
    this.CURRENCY_SYMBOLS = {
      'TWD': 'NT$',
      'HKD': 'HK$',
      'CNY': 'CN¥',
      'JPY': 'JP¥',
      'USD': 'US$'
    }
  }

  getDomCurrency() {
    const node = document.querySelector('meta[name="current-currency"]')
    return node ? node.getAttribute('content') : null
  }

  async fetchCurrencyInfo(force = false) {
    const domCurrency = this.getDomCurrency()
    const cachedCurrency = this.currencyInfo?.data?.current_currency
    const shouldUseCache = !force && this.currencyInfo && (!domCurrency || cachedCurrency === domCurrency)

    if (shouldUseCache) {
      return this.currencyInfo
    }

    if (this.pendingFetch) {
      return this.pendingFetch
    }

    this.pendingFetch = axios.get('/api/v1/currencies/info')
      .then((response) => {
        this.currencyInfo = response.data
        return this.currencyInfo
      })
      .catch((error) => {
        console.error('Failed to fetch currency info:', error)

        const fallbackCurrency = domCurrency || 'TWD'
        const fallbackSymbol = this.CURRENCY_SYMBOLS[fallbackCurrency] || 'NT$'

        if (this.currencyInfo?.data) {
          this.currencyInfo.data.current_currency = fallbackCurrency
          this.currencyInfo.data.symbol = fallbackSymbol
        } else {
          this.currencyInfo = {
            data: {
              current_currency: fallbackCurrency,
              symbol: fallbackSymbol,
              name: fallbackCurrency,
              exchange_rates: {}
            }
          }
        }

        return this.currencyInfo
      })
      .finally(() => {
        this.pendingFetch = null
      })

    return this.pendingFetch
  }

  invalidateCache() {
    this.currencyInfo = null
  }

  // 取得貨幣精度 (小數位數)
  getCurrencyPrecision(currency) {
    return this.CURRENCY_PRECISION[currency] || 0
  }

  convertPrice(price, fromCurrency = 'TWD', toCurrency = null) {
    if (!this.currencyInfo || !this.currencyInfo.data) return price

    const targetCurrency = toCurrency || this.currencyInfo.data.current_currency
    if (!targetCurrency || fromCurrency === targetCurrency) return price
    const rates = this.currencyInfo.data.exchange_rates
    if (!rates || !rates[targetCurrency]) return price

    // 取得目標貨幣的精度
    const precision = this.getCurrencyPrecision(targetCurrency)

    // 從 TWD 轉換到目標幣別
    if (fromCurrency === 'TWD') {
      // 匯率格式：1 TWD = rate 目標貨幣，所以使用乘法
      const convertedPrice = price * rates[targetCurrency]
      return Math.round(convertedPrice * Math.pow(10, precision)) / Math.pow(10, precision)
    }

    // 從其他幣別轉換到 TWD，再轉換到目標幣別
    const twdPrice = price / rates[fromCurrency]
    const convertedPrice = twdPrice * rates[targetCurrency]
    return Math.round(convertedPrice * Math.pow(10, precision)) / Math.pow(10, precision)
  }

  async displayPrice(price, fromCurrency = 'TWD') {
    if (price === null || price === undefined || price === '' || Number(price) === 0 || isNaN(Number(price))) {
      return "--"
    }

    const info = await this.fetchCurrencyInfo()
    const convertedPrice = this.convertPrice(price, fromCurrency)
    const symbol = info?.data?.symbol || 'NT$'
    const currentCurrency = info?.data?.current_currency || 'TWD'
    const precision = this.getCurrencyPrecision(currentCurrency)
    const formattedPrice = Number(convertedPrice || 0).toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })

    return `${symbol} ${formattedPrice}`
  }

  async loadCurrencyInfo() {
    try {
      const info = await this.fetchCurrencyInfo()
      const data = info?.data || {}

      return {
        currencyCode: data.current_currency || 'TWD',
        currencySymbol: data.symbol || 'NT$'
      }
    } catch (error) {
      console.error('載入幣別資訊失敗：', error)

      return {
        currencyCode: 'TWD',
        currencySymbol: 'NT$'
      }
    }
  }
}

export const currencyHelper = new CurrencyHelper()

document.addEventListener('turbo:render', () => {
  const domCurrency = currencyHelper.getDomCurrency()
  const cachedCurrency = currencyHelper.currencyInfo?.data?.current_currency

  if (domCurrency && cachedCurrency && domCurrency !== cachedCurrency) {
    currencyHelper.invalidateCache()
  }
})

document.addEventListener('turbo:before-cache', () => {
  currencyHelper.invalidateCache()
})
